import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useState,
} from "react";
import { AUTH_TYPES, FORMS, MODULES } from "../../const";
import LoadingSpinner from "../LoadingSpinner";
import Input from "../input/Input";
import Dropdown from "../input/Dropdown";
import NumberStepper from "../input/NumberStepper";
import Button from "../input/Button";
import ButtonRenderer from "../renderer/ButtonRenderer";
import Grid from "../Grid";
import { isNumber } from "../../helper";
import {toast} from "react-toastify";
import {optionConfigValidation} from "../validations/OptionConfigCreation";

const FORM = FORMS.OPTION_CONFIG.OPT.form;

const newFormRow = (keyKey, keyCode) => ({
    id: null,
    keyId: null,
    keyKey,
    keyCode,
    keyValue1: "",
    keySeq: null,
    keyDescription: "",
    keyStatus: 2,
    keyActive: 1,
});

const OptionAddComponent = forwardRef(
    ({ errors = {}, dropdown, isLoading, currentValues, editMode }, ref) => {
        const [typeKey, setTypeKey] = useState(currentValues?.keyKey || "");
        const [typeCode, setTypeCode] = useState("");
        const [rows, setRows] = useState([]);
        const [deletedIds, setDeletedIds] = useState([]);
        const [formRow, setFormRow] = useState(newFormRow(typeKey, typeCode));
        const [localId, setLocalId] = useState(2);
        const [currentErrors, setCurrentErrors] = useState({});
        const isEditing = Boolean(formRow?.id || formRow?.keyId);

        useEffect(() => {
            setCurrentErrors(errors || {});
        }, [errors]);

        const typeMeta = useMemo(() => {
            return dropdown?.optionTypesOpt?.find((o) => o.value === typeKey) || null;
        }, [dropdown?.optionTypesOpt, typeKey]);

        useEffect(() => {
            setTypeCode(typeMeta?.code || "");
        }, [typeMeta]);

        useEffect(() => {
            if (editMode && Array.isArray(currentValues?.detail)) {
                const seeded = currentValues.detail.map((r, idx) => ({
                    ...r,
                    id: idx + 2,
                }));
                setRows(seeded);
                setLocalId(seeded.length + 3);
                setTypeKey(currentValues.keyKey || "");
                setFormRow(newFormRow(currentValues.keyKey || "", typeMeta?.code || ""));
            } else {
                setRows([]);
                setLocalId(2);
                setFormRow(newFormRow(typeKey, typeMeta?.code || ""));
            }
        }, [editMode, currentValues]);

        useImperativeHandle(ref, () => ({
            resetForm: () => {
                setTypeKey("");
                setTypeCode("");
                setRows([]);
                setDeletedIds([]);
                setLocalId(2);
                setFormRow(newFormRow("", ""));
                setCurrentErrors({});
            },
            validateForm: () => {
                return {};
            },
            getBulkData: () => {
                const strip = ({ id, modified, statusLabel, activeLabel, ...rest }) => ({
                    ...rest,
                    keyKey: typeKey,
                    keyCode: typeCode,
                });

                const created  = rows.filter(r => !r.keyId).map(strip);
                const modified = rows.filter(r => r.keyId && r.modified).map(strip);
                const deleted  = deletedIds.slice();

                return { created, modified, deleted };
            },
            getData: () => ({ keyKey: typeKey, keyCode: typeCode }),
        }));

        const handleTypeChange = (e, data) => {
            const selected = (dropdown.optionTypesOpt || []).find(o => o.value === data.value);
            const Key = data.value;
            const Code = selected?.code || "";

            setTypeKey(Key);
            setTypeCode(Code);

            setRows([]);
            setDeletedIds([]);
            setLocalId(2);
            setFormRow(newFormRow(Key, Code));
        };



        const resetFormOnly = () => {
            setFormRow(newFormRow(typeKey, typeCode));
            setCurrentErrors({});
        };

        const resetAll = () => {
            setTypeKey("");
            setTypeCode("");
            setRows([]);
            setDeletedIds([]);
            setLocalId(2);
            setFormRow(newFormRow("", ""));
            setCurrentErrors({});
        };


        const handleAddOrSave = () => {
            const trimmedName = (formRow.keyValue1 || "").trim();

            const fieldErrors = optionConfigValidation({
                keyKey: typeKey,
                keyValue1: trimmedName,
                keyStatus: formRow.keyStatus,
                keyActive: formRow.keyActive,
            });

            if (Object.keys(fieldErrors).length > 0) {
                setCurrentErrors(fieldErrors);
                Object.values(fieldErrors).forEach(msg =>
                    toast.error(msg, { autoClose: 4000, containerId: "Main" })
                );
                return;
            }

            const thisLocalId = formRow.id ?? null;
            const thisDbId = formRow.keyId ?? null;

            const isSameRow = (r) =>
                (thisDbId ? r.keyId === thisDbId : false) ||
                (thisLocalId ? r.id === thisLocalId : false);

            const nameClash =
                trimmedName.length > 0 &&
                rows.some(
                    (r) =>
                        !isSameRow(r) &&
                        (r.keyValue1 || "").trim().toLowerCase() === trimmedName.toLowerCase()
                );

            const seqClash =
                formRow.keySeq !== undefined &&
                formRow.keySeq !== null &&
                rows.some(
                    (r) => !isSameRow(r) && Number(r.keySeq) === Number(formRow.keySeq)
                );

            let hasError = false;
            if (nameClash) {
                toast.error("Option name already exists for this type.", {
                    autoClose: 4000,
                    containerId: "Main",
                });
                hasError = true;
            }
            if (seqClash) {
                toast.error("Sort order must be unique for this type.", {
                    autoClose: 4000,
                    containerId: "Main",
                });
                hasError = true;
            }
            if (hasError) return;

            const payload = {
                ...formRow,
                keyKey: typeKey,
                keyCode: typeCode,
            };

            const statusLabel =
                (dropdown.statusOptions || []).find(
                    (o) => String(o.value) === String(payload.keyStatus)
                )?.text ?? "";
            const activeLabel =
                (dropdown.activeOptions || []).find(
                    (o) => String(o.value) === String(payload.keyActive)
                )?.text ?? "";

            const rowToInsert = { ...payload, statusLabel, activeLabel };

            if (rowToInsert.keyId) {
                setRows((prev) =>
                    prev.map((r) =>
                        r.keyId === rowToInsert.keyId
                            ? { ...rowToInsert, modified: true, id: r.id }
                            : r
                    )
                );
                resetFormOnly();
                return;
            }

            if (rowToInsert.id) {
                setRows((prev) =>
                    prev.map((r) => (r.id === rowToInsert.id ? { ...rowToInsert } : r))
                );
                resetFormOnly();
                return;
            }

            setRows((prev) => [...prev, { ...rowToInsert, id: localId }]);
            setLocalId((n) => n + 1);
            resetFormOnly();
        };

        return isLoading ? (
            <LoadingSpinner />
        ) : (
            <form className="ui form" style={{ overflowY: "auto", height: "75vh" }}>
                <div className="ui segment">
                    <h4 className="ui dividing header">Option Config</h4>

                    <div className="two fields">
                        <Dropdown
                            required
                            label="Option Type"
                            error={currentErrors.keyKey}
                            editMode={true}
                            module={MODULES.Admin}
                            form={FORM}
                            field="key_key"
                            options={dropdown.optionTypesOpt}
                            value={typeKey}
                            placeholder="Option Type"
                            onChange={handleTypeChange}
                            disabled={editMode}
                        />
                        <Input
                            label="Prefix"
                            module={MODULES.Admin}
                            form={FORM}
                            field="key_code"
                            value={typeCode}
                            disabled
                            readOnly
                        />
                    </div>

                    <div className={isEditing ? "two fields" : "one field"}>
                        <Input
                            required
                            label="Option Name"
                            module={MODULES.Admin}
                            form={FORM}
                            field="key_value1"
                            error={currentErrors.keyValue1}
                            value={formRow.keyValue1}
                            onChange={(e) =>
                                setFormRow((prev) => ({ ...prev, keyValue1: e.target.value }))
                            }
                        />

                        {isEditing && (
                            <NumberStepper
                                label="Sort Order"
                                value={formRow.keySeq ?? null}
                                disabled
                                readOnly
                                onChange={() => {}}
                                min={0}
                                step={1}
                            />
                        )}
                    </div>

                    <div className="two fields">
                        <Input
                            label="Description"
                            module={MODULES.Admin}
                            form={FORM}
                            field="key_description"
                            value={formRow.keyDescription}
                            onChange={(e) =>
                                setFormRow((prev) => ({
                                    ...prev,
                                    keyDescription: e.target.value,
                                }))
                            }
                        />
                    </div>

                    <div className="two fields">
                        <Dropdown
                            required
                            label="Status"
                            module={MODULES.Admin}
                            form={FORM}
                            field="key_status"
                            options={dropdown.statusOptions}
                            value={formRow.keyStatus}
                            onChange={(e, data) =>
                                setFormRow((prev) => ({ ...prev, keyStatus: data.value }))
                            }
                        />
                        <Dropdown
                            required
                            label="Active"
                            module={MODULES.Admin}
                            form={FORM}
                            field="key_active"
                            options={dropdown.activeOptions}
                            value={formRow.keyActive}
                            onChange={(e, data) =>
                                setFormRow((prev) => ({ ...prev, keyActive: data.value }))
                            }
                        />
                    </div>

                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            justifyContent: "flex-end",
                        }}
                    >
                        <Button template="reset" onClick={resetAll} />
                        <Button
                            template="addOrSave"
                            authenticated={true}
                            editMode={editMode}
                            module={MODULES.Admin}
                            form={`${FORM}`}
                            create={!formRow.keyId && !formRow.id}
                            onClick={handleAddOrSave}
                        />
                    </div>

                    <Grid
                        heading={`Options List`}
                        config={[
                            { headerName: "Option Name", field: "keyValue1" },
                            { headerName: "Prefix", field: "keyCode" },
                            { headerName: "Sort Order", field: "keySeq" },
                            { headerName: "Status", field: "statusLabel" },
                            { headerName: "Active", field: "activeLabel" },
                            {
                                field: "Edit",
                                filter: true,
                                cellRenderer: ButtonRenderer,
                                cellRendererParams: (params) => ({
                                    consider: AUTH_TYPES.CREATE,
                                    clickHandler: () => setFormRow({ ...params.data }),
                                    label: "Edit",
                                    authenticated: true,
                                    form: `${FORM}`,
                                    module: MODULES.Admin,
                                    editMode: true,
                                }),
                            },
                            {
                                field: "Delete",
                                filter: true,
                                cellRenderer: ButtonRenderer,
                                cellRendererParams: (params) => ({
                                    consider: AUTH_TYPES.DELETE,
                                    clickHandler: () => {
                                        if (params.data.keyId && isNumber(params.data.keyId)) {
                                            setDeletedIds((prev) => [...prev, params.data.keyId]);
                                        }
                                        setRows((prev) => prev.filter((r) => r.id !== params.data.id));
                                    },
                                    label: "Delete",
                                    className: "ui red button",
                                    authenticated: true,
                                    form: `${FORM}`,
                                    module: MODULES.Admin,
                                    editMode: true,
                                }),
                            },
                        ]}
                        content={rows}
                    />
                </div>
            </form>
        );
    }
);

export default OptionAddComponent;